import { useNavigate } from 'react-router-dom';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import Button from '../shared/components/buttons';
import { PageProps } from '../shared/defines';
import { ProfileHeightIcon } from '../shared/components/icons/ProfileHeightIcon';
import { ReactComponent as DottedArrowLeft } from '../shared/components/icons/dotted-arrow-left.svg';
import { ReactComponent as DottedArrowRight } from '../shared/components/icons/dotted-arrow-right.svg';
import { childHeightOptionState, MEASURE_RANGE_VALUES } from '../state/child-details';
import { HeightMeasureInput } from '../components/HeightMeasureInput';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { v4 as uuidv4 } from 'uuid';
import { TextDecoder } from '../shared/text-effects/TextDecoder';
import { useTextSize } from '../shared/hooks/useTextSize';

const maxMeasure = 25;
const minMeasure = 0;

const getSelectedValueLabel = (value: number, heightUnit: 'cm' | 'in') => {
  const findRange = MEASURE_RANGE_VALUES.find((range) => range.average === value);

  return heightUnit === 'cm' ? findRange?.cmLabel : findRange?.inLabel;
};

const HeightPage: FC<PageProps> = ({ title }) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  const hiddenRef = useRef<HTMLHeadingElement>(null);
  const { height: titleHeight } = useTextSize(hiddenRef);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const [heightAverage, setHeightAverage] = useRecoilState(childHeightOptionState);
  const heightOptions = [...MEASURE_RANGE_VALUES].reverse();
  const [heightUnit, setHeightUnit] = useState<'in' | 'cm'>('in');

  const selectedCarouselIndex = heightOptions.findIndex((option) => option.average === heightAverage);

  const uuids = useMemo(() => {
    return heightOptions.map(() => uuidv4());
  }, [selectedCarouselIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchUnit = () => {
    if (heightUnit === 'cm') {
      setHeightUnit('in');
    } else {
      setHeightUnit('cm');
    }
  };

  const handleEnter = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      btnRef.current?.click();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleEnter);

    return () => {
      window.removeEventListener('keydown', handleEnter);
    };
  }, [handleEnter]);

  useEffect(() => {
    document.title = title;
  }, [title]);

  const onContinueButtonClick = async () => {
    navigate('/confirm-permission');
  };

  return (
    <div className="w-full flex flex-col flex-1 items-start">
      <DndProvider backend={HTML5Backend}>
        <div className="w-full flex font-medium">
          <div className="whitespace-normal w-full" style={{ height: `${titleHeight}px` }}>
            <TextDecoder
              className="font-aeonik font-medium text-[2.125rem]/[2.125rem] sm:text-[3.1rem]/[3.4rem] gap-5"
              delay={0}
              duration={500}
              animationType={'in'}
              text="Select child height"
            />
          </div>
          <h1
            className="absolute opacity-0 top-0 pointer-events-none font-aeonik font-medium text-[2.125rem]/[2.125rem] sm:text-[3.1rem]/[3.4rem]"
            ref={hiddenRef}
          >
            Select child height
          </h1>
        </div>
        {!isMobile && (
          <div className="relative w-full flex flex-col items-center mt-10 mb-5">
            <div className="flex justify-center mt-10 text-[3.125rem]/[3.375rem] w-full font-medium">
              {getSelectedValueLabel(heightAverage, heightUnit)}
            </div>

            <div className="flex w-full justify-center h-108">
              <div className="h-full flex items-end">
                <ProfileHeightIcon className="animate-pop-in" height={heightAverage} />
              </div>

              <HeightMeasureInput
                value={heightAverage}
                unit={heightUnit}
                onChange={setHeightAverage}
                max={maxMeasure}
                min={minMeasure}
              />
            </div>
          </div>
        )}
        {isMobile && (
          <Carousel
            className="w-full mt-6 [&_.carousel]:flex relative"
            showThumbs={false}
            showIndicators={false}
            showStatus={false}
            swipeScrollTolerance={50}
            preventMovementUntilSwipeScrollTolerance={true}
            onChange={(index) => setHeightAverage(heightOptions[index].average)}
            selectedItem={selectedCarouselIndex}
            renderArrowNext={(clickHandler, hasNext) => {
              return (
                <button className="absolute z-50 bottom-0 right-0" onClick={clickHandler}>
                  {hasNext && <DottedArrowRight />}
                </button>
              );
            }}
            renderArrowPrev={(clickHandler, hasPrev) => {
              return (
                <button className="absolute z-50 bottom-0 left-0" onClick={clickHandler}>
                  {hasPrev && <DottedArrowLeft />}
                </button>
              );
            }}
          >
            {heightOptions.map((obj, index) => (
              <div className="flex flex-col justify-between items-center h-100" key={index}>
                <ProfileHeightIcon
                  className="animate-pop-in-mobile scale-90"
                  key={uuids[index] || ''}
                  height={obj.average}
                  style={{ animationDelay: '300ms' }}
                />
                <div className="w-full flex h-11 mt-5 justify-center text-[1.875rem]/[1.875rem]">
                  {heightUnit === 'cm' ? heightOptions[index].cmLabel : heightOptions[index].inLabel}
                </div>
              </div>
            ))}
          </Carousel>
        )}
        <div className="flex justify-center w-full text-green-laser font-medium text-[1.125rem] py-8">
          <button className="hover:opacity-70 focus:opacity-50" onClick={switchUnit}>
            Switch units
          </button>
        </div>
        <div className="flex flex-col gap-5 w-full">
          <div className="text-white w-full text-[1.25rem]/[1.5rem] font-medium py-2 border-b-2 border-white">
            We use this to improve your child's experience
          </div>
          <Button
            ref={btnRef}
            variant="filled"
            size="sm"
            className="text-[1.2rem] w-full sm:w-57.5"
            onClick={onContinueButtonClick}
          >
            Continue
          </Button>
        </div>
      </DndProvider>
    </div>
  );
};

export default HeightPage;
